import { PermissionServiceResponseModel, Permission, CreatePermissionServiceModel, UpdatePermissionServiceModel } from '../types';
import { detectFormChanges } from './changeDetector';

/**
 * Maps API permission response to UI permission model
 */
export const mapApiPermissionToUi = (apiPermission: PermissionServiceResponseModel): Permission => {
  // Extract role IDs and names from the API response if available
  const roleIds = apiPermission.roles?.map(role => role.id) || [];
  const roleNames = apiPermission.roles?.map(role => role.name) || [];
  
  return {
    id: apiPermission.id,
    permissionId: apiPermission.id, // Using the same ID for now
    name: apiPermission.name,
    description: apiPermission.description,
    roles: roleIds,
    roleNames: roleNames
  };
};

/**
 * Maps UI permission form data to API create model
 */
export const mapUiToCreatePermissionApiModel = (formData: {
  name: string;
  description: string;
  roles: string[];
}): CreatePermissionServiceModel => {
  return {
    name: formData.name,
    description: formData.description,
    roleIds: formData.roles, // Map roles array to roleIds
  };
};

/**
 * Maps UI permission form data to API update model
 */
export const mapUiToUpdatePermissionApiModel = (formData: {
  name: string;
  description: string;
  roles: string[];
}): UpdatePermissionServiceModel => {
  return {
    name: formData.name,
    description: formData.description,
    roleIds: formData.roles, // Map roles array to roleIds
  };
};

/**
 * Maps UI permission form data to API update model with only changed fields for PATCH requests
 * Note: roleIds are excluded from this payload as role assignments are managed separately
 * via PATCH /permissions/{id}/roles endpoint
 */
export const mapUiToUpdatePermissionApiModelPatch = (
  originalPermission: Permission,
  formData: {
    name: string;
    description: string;
    roles: string[];
  }
): UpdatePermissionServiceModel => {
  // Create the full update object (API model) - EXCLUDE roleIds
  // Role assignments are handled separately via /permissions/{id}/roles endpoint
  const fullUpdate: UpdatePermissionServiceModel = {
    name: formData.name,
    description: formData.description,
    // roleIds is intentionally excluded - handled via separate endpoint
  };

  // Create the original object for comparison (also API model) - EXCLUDE roleIds
  const originalApiModel: UpdatePermissionServiceModel = {
    name: originalPermission.name,
    description: originalPermission.description,
    // roleIds is intentionally excluded - handled via separate endpoint
  };

  // Return only the changed fields by comparing API models (name and description only)
  return detectFormChanges(originalApiModel, fullUpdate);
};
